import { DefaultButton, Link, Text } from '@fluentui/react';
import { useCustomMessageModal } from 'app/components/customMessageModal/CustomMessageModal';
import { usePostCommentsHashTags } from 'app/services/mutations';
import { 
    useEditComment,
    useEditStatusResponse,
    useSubmitComment,
    useSubmitStatus,
    useSubmitStatuses
} from 'app/services/request-api';
import {
    CreateStatusDto,
    ICommentDto,
    ICreateStatusDto,
    ICreateStatusesDto,
    RequestDto,
    UserDto
} from 'generated/request-backend';
import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { getURLForTeamAndType } from '../helpers';
import RequestRichTextEditor from 'app/modules/admin/detailsPanel/requestRichTextEditor/RequestRichTextEditor';
import _, { isEmpty } from 'lodash';
import './CommentField.scss';
import { removeBlobFileSasToken } from 'app/utils/blobStorageHelpers';
import { appInsightsService, BlobStorageContainer } from 'app/utils/constants';
import { HashTagDto } from 'generated/crew-backend';
import { useGetUsersList } from 'app/services/queries';
import { GraphUser } from 'generated/user-backend';

interface IProps {
    id?: string;
    isEditActive?: boolean;
    initialValue?: string;
    correlatedChildrenIds?: string[];
    correlationId?: string;
    entityType?: number;
    cancelOperation?: Function;
    textareaSize?: number;
    className?: string;
    isInternal?: boolean;
    fieldType?: string;
    selectedRequest?: RequestDto;
    parentCommentId?: string;
    parentStatusId?: string;
    autoFocus?: boolean;
}

const CommentField: React.FC<IProps> = ({
    id,
    isEditActive,
    initialValue,
    correlatedChildrenIds,
    correlationId,
    parentCommentId,
    entityType,
    cancelOperation,
    className,
    isInternal,
    fieldType,
    selectedRequest,
    autoFocus = true
}) => {
    // state variable
    // user type is admin for internal comments/statuses
    const [userType, setUserType] = useState<"Admin" | null>(null);
    const [hashTagsFormatted, setHashTagsFormatted] = useState<HashTagDto[]>([]);

    const [commentText, setCommentText] = useState<string>(initialValue ? initialValue : '');
    const [textToSubmit, setTextToSubmit] = useState<string>('');

    const [mentionedUsers, setMentionedUsers] = useState<UserDto[]>([]);

    const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true);

    const { CustomMessageModal, showModal: displayErrorModal } = useCustomMessageModal();

    // queries
    const { mutate: submitStatusesMutation, isSuccess: succesStatuses, isError: isErrorSubmitStatuses } = useSubmitStatuses();
    const {
        mutate: editStatusResponseMutation,
        isSuccess: editStatusResponseSuccess,
        isError: isErrorStatusResponse
    } = useEditStatusResponse();
    const { mutate: editCommentMutation, isSuccess: editCommentSuccess, isError: isErrorEditComment } = useEditComment();
    const { mutate: submitCommentMutation, isSuccess: submitSuccess, isError: isErrorSubmitComment } = useSubmitComment();
    const { mutate: submitStatusMutation, isSuccess: successStatus, isError: isErrorSubmitStatus } = useSubmitStatus();
    const { mutate: postHashTags } = usePostCommentsHashTags();
    let { data: adminUsersList } = useGetUsersList("", false, "Admin");

    useEffect(() => {
        if (isInternal) {
            setUserType('Admin');
        } else {
            setUserType(null);
        }
    }, [isInternal]);

    useEffect(() => {
        if (editCommentSuccess || editStatusResponseSuccess) {
            if (cancelOperation) cancelOperation();
        }
    }, [editCommentSuccess, editStatusResponseSuccess, selectedRequest]);

    useEffect(() => {
        if (
            (successStatus || succesStatuses || submitSuccess || editCommentSuccess || editStatusResponseSuccess) &&
            selectedRequest
        ) {
            let url = getURLForTeamAndType(
                selectedRequest.requestTeam,
                selectedRequest.requestType
            );
            if (Array.isArray(hashTagsFormatted) && hashTagsFormatted?.length > 0) {
                postHashTags({ url: url, data: hashTagsFormatted, id: correlationId });
            }
        }
    }, [successStatus, succesStatuses, submitSuccess, editCommentSuccess, editStatusResponseSuccess, selectedRequest]);

    useEffect(() => {
        if (isErrorSubmitComment || isErrorSubmitStatus || isErrorSubmitStatuses || isErrorEditComment || isErrorStatusResponse) {
            displayErrorModal();
        }
    }, [isErrorSubmitComment, isErrorSubmitStatus, isErrorSubmitStatuses, isErrorEditComment, isErrorStatusResponse]);
    
    const handleSubmit = () => {
        let data: ICommentDto = {
            // TODO: Split this into two data types - Comment and Status
            // This could either be an ICommentDto or an IStatusDto...
            // If we update handleSubmit to be either a comment or a status, then we can make this all cleaner.
            // hashtags: formatHashtags(hashtags),
            correlationId: correlationId,
            entityType: entityType,
            isInternal: isInternal,
            taggedUsers: _.uniq(mentionedUsers),
            // id: id ?? null,
            // parentStatusId: parentStatusId ?? null,
            parentCommentId: parentCommentId ?? null
        };

        if (commentText || textToSubmit) {
            if (isEditActive) {
                if (fieldType === 'comment') {
                    data.commentText = textToSubmit !== '' ? removeBlobFileSasToken(textToSubmit.trim()) : removeBlobFileSasToken(commentText.trim());
                    editCommentMutation(data);
                } else {
                    data.commentText = textToSubmit !== '' ? removeBlobFileSasToken(textToSubmit.trim()) : removeBlobFileSasToken(commentText.trim());
                    const statusResponse : ICreateStatusDto = { ...data, correlationId: data.correlationId, statusText: data.commentText, id:id };
                    editStatusResponseMutation(statusResponse);
                }
            } else {
                if (fieldType === 'comment') {
                    data.commentText = textToSubmit !== '' ? removeBlobFileSasToken(textToSubmit.trim()) : removeBlobFileSasToken(commentText.trim());
                    submitCommentMutation(data);
                } else {
                    data.commentText = textToSubmit !== '' ? removeBlobFileSasToken(textToSubmit.trim()) : removeBlobFileSasToken(commentText.trim());
                    if (correlatedChildrenIds.length > 0) {
                        const statusResponse : ICreateStatusesDto = {
                            parentStatus: new CreateStatusDto({ ...data, correlationId: selectedRequest.unifiedTrackingId, statusText:data.commentText }),
                            childStatuses: correlatedChildrenIds.map((correlationId) => (new CreateStatusDto({ ...data, correlationId, statusText:data.commentText })))
                        }
                        submitStatusesMutation(statusResponse);
                    } else {
                        const statusResponse : ICreateStatusDto = { ...data, statusText: data.commentText }
                        submitStatusMutation(statusResponse);
                    }
                }
            }

            // log number of admin users mentioned and number of non admin users mentioned
            if (!isEmpty(mentionedUsers)) {
                adminUsersList = Array.isArray(adminUsersList) ? adminUsersList : [];
                let numberOfAdminUsersMentioned = 0;
                let numberOfNonAdminUsersMentioned = 0;
                let totalAdminUserCount = adminUsersList.length;
                mentionedUsers.forEach(user => {
                    if (adminUsersList.some((adminUser: GraphUser) => adminUser.id === user.id)) {
                        numberOfAdminUsersMentioned++;
                    } else {
                        numberOfNonAdminUsersMentioned++;
                    }
                });

                if (numberOfAdminUsersMentioned > 0 || numberOfNonAdminUsersMentioned > 0) {
                    // log only if at least one user is mentioned
                    appInsightsService.logEvent('UserCountMentionedInComments', {
                        "AdminCount": numberOfAdminUsersMentioned.toString(),
                        "NonAdminCount": numberOfNonAdminUsersMentioned.toString(),
                        "TotalAdminUserCount": totalAdminUserCount.toString()
                    });
                }
            }
        }

        setCommentText('');
        setTextToSubmit('');
        setMentionedUsers([]);
        if (cancelOperation) cancelOperation();
    };

    const handleCancel = () => {
        setCommentText('');
        setTextToSubmit('');
        setMentionedUsers([]);
        if (cancelOperation) cancelOperation();
    };


    useEffect(() => {
        if (isInternal) {
            setUserType('Admin');
        } else {
            setUserType(null);
        }
    }, [isInternal]);

    useEffect(() => {
        if (editCommentSuccess || editStatusResponseSuccess) {
            if (cancelOperation) cancelOperation();
        }
    }, [editCommentSuccess, editStatusResponseSuccess, selectedRequest]);

    useEffect(() => {
        if (
            (successStatus || succesStatuses || submitSuccess || editCommentSuccess || editStatusResponseSuccess) &&
            selectedRequest
        ) {
            let url = getURLForTeamAndType(selectedRequest.requestTeam, selectedRequest.requestType);
            if (Array.isArray(hashTagsFormatted) && hashTagsFormatted?.length > 0) {
                postHashTags({ url: url, data: hashTagsFormatted, id: correlationId });
            }
        }
    }, [successStatus, succesStatuses, submitSuccess, editCommentSuccess, editStatusResponseSuccess, selectedRequest]);

    useEffect(() => {
        if (isErrorSubmitComment || isErrorSubmitStatus || isErrorSubmitStatuses || isErrorEditComment || isErrorStatusResponse) {
            displayErrorModal();
        }
    }, [isErrorSubmitComment, isErrorSubmitStatus, isErrorSubmitStatuses, isErrorEditComment, isErrorStatusResponse]);

    return (
        <div className={`${className}`} aria-label='add a comment'>
            <CustomMessageModal />
            <div className='comment-textfield'>
                <RequestRichTextEditor
                    content={commentText}
                    isMentionAvailable={true}
                    setMentionedUsers={setMentionedUsers}
                    mentionedUsers={mentionedUsers}
                    hashTagsFormatted={hashTagsFormatted}
                    setHashTagsFormatted={setHashTagsFormatted}
                    isCommentField={true}
                    setIsSubmitDisabled={setIsSubmitDisabled}
                    setTextToSubmit={setTextToSubmit}
                    textToSubmit={textToSubmit}
                    fileStorageContainer={BlobStorageContainer.COMMENT_STATUS_FILES}
                    userType={userType}
                    autoFocus={autoFocus}
                />
            </div>
            {isEditActive ? (
                <div className="cmt-action">
                    {((commentText && commentText.trim().length > 0) || (textToSubmit && textToSubmit.trim().length > 0)) && (
                        <Link className="action-items" data-testid="reply-one" tabIndex={0} onClick={handleSubmit}>
                            Save
                        </Link>
                    )}

                    {(commentText.trim().length === 0 || textToSubmit.trim().length === 0) && (
                        <Link className="action-items" data-testid="reply-one" tabIndex={0} disabled>
                            Save
                        </Link>
                    )}

                    <Text className="action-items">
                        <i className="ms-Icon ms-Icon--LocationDot" aria-hidden="true"></i>
                    </Text>
                    <Link className="action-items" data-testid="reply-one" tabIndex={0} onClick={() => handleCancel()}>
                        Cancel
                    </Link>
                </div>
            ) : (
                <>
                    <div className="comment-form-btn-wrap">
                        <DefaultButton className="default" onClick={handleCancel}>
                            <Trans>Cancel</Trans>
                        </DefaultButton>
                        <DefaultButton type="submit" className="primary" onClick={handleSubmit}
                            disabled={isSubmitDisabled}
                        >
                            <Trans>Submit</Trans>
                        </DefaultButton>
                    </div>
                </>
            )}
        </div>
    );
};

export default CommentField;
